import { useEffect, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useAuth } from '../features/auth/use-auth.js';
import { Button } from '../components/ui/button.js';
import { Input } from '../components/ui/input.js';
import { Label } from '../components/ui/label.js';
import { EmberField } from '../components/visual/ember-field.js';
import { HestiaLogo } from '../components/hestia-logo.js';

const IS_DEV = import.meta.env.DEV;
// External link target shown on the "link sent" screen in dev.
const MAILHOG_URL = 'http://localhost:8025';

function LoginPage() {
  const { requestMagicLink } = useAuth();
  const [email, setEmail] = useState('');

  // The login screen is a light, image-led marketing surface — its right-hand
  // panel is always cream. If the user logs out while in dark mode the root
  // keeps data-theme="dark", so the token-driven text turns light-on-light and
  // becomes invisible. Force light here; restore the stored preference on leave
  // (the /app layout re-applies it on its own when the user signs back in).
  useEffect(() => {
    const root = document.documentElement;
    const previous = root.getAttribute('data-theme');
    root.removeAttribute('data-theme');
    return () => {
      if (previous) root.setAttribute('data-theme', previous);
    };
  }, []);
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await requestMagicLink(email);
      setSent(true);
    } catch {
      // Network failure — keep the form interactive so the user can retry
      // instead of leaving the button stuck on "Küldés…".
      setError('Nem sikerült elküldeni a linket. Ellenőrizd a kapcsolatot, és próbáld újra.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="h2-login-shell">
      {/* LEFT — editorial hero with drifting embers */}
      <div
        className="h2-login-art hidden lg:block"
        style={{
          backgroundImage:
            'url(https://images.unsplash.com/photo-1582719508461-905c673771fd?w=1600&q=80)',
        }}
      >
        <EmberField count={18} />
        <div className="h2-login-art-content">
          <div className="flex items-center gap-3">
            <span className="h2-ember-host">
              <HestiaLogo size={40} wordmarkColor="var(--brand-50)" />
            </span>
          </div>

          <div>
            <div className="h2-eyebrow mb-4" style={{ color: 'rgba(236,249,240,0.6)' }}>
              Belső munkafelület · v0.4
            </div>
            <h1
              className="h2-display"
              style={{ color: 'var(--brand-50)', fontSize: 52, margin: 0, maxWidth: 540 }}
            >
              Tudatos és tervezhető kommunikáció.
              <em
                style={{
                  color: 'var(--accent-highlight)',
                  fontStyle: 'normal',
                  display: 'block',
                  marginTop: '0.35em',
                }}
              >
                Hestia by Morgens.
              </em>
            </h1>
            <p
              style={{
                marginTop: 18,
                fontSize: 15,
                lineHeight: 1.5,
                color: 'rgba(236,249,240,0.75)',
                maxWidth: 460,
              }}
            >
              A Morgens belső stratégiai és content tervező platformja. Tartalomtervezés és
              kommunikációs menedzsment — minden ügyfelünknek, egy helyen.
            </p>
          </div>

          <div
            className="flex flex-wrap items-center text-meta"
            style={{ color: 'rgba(236,249,240,0.55)' }}
          >
            <span className="font-mono ml-auto">morgens.hu / hestia</span>
          </div>
        </div>
      </div>

      {/* RIGHT — auth card */}
      <div className="flex items-center justify-center px-4 py-10 sm:px-8">
        <div className="w-full max-w-[400px]">
          {/* Mobile-only logo: full editorial hero hidden under lg */}
          <div className="mb-8 flex items-center lg:hidden">
            <HestiaLogo size={40} />
          </div>

          <div className="h2-eyebrow">Belépés</div>
          <h1 className="mt-2 font-serif text-display font-normal leading-[1] tracking-[-0.02em] text-[var(--ink-700)]">
            Üdv vissza<span className="text-[var(--brand-500)]">.</span>
          </h1>
          <p className="mt-2 text-body text-[var(--fg-2)]">
            Add meg az email-címedet — küldünk egy egyszer használatos linket (15 percig érvényes).
          </p>

          <div className="mt-8 rounded-lg border border-[var(--app-divider)] bg-[var(--n-0)] p-6 shadow-1">
            {sent ? (
              <div className="space-y-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[var(--brand-100)] text-[var(--brand-800)]">
                  <svg
                    width={18}
                    height={18}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={1.75}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                </div>
                <h2 className="text-h2 font-semibold text-[var(--ink-700)]">Link elküldve</h2>
                <p className="text-body text-[var(--fg-2)]">
                  Ha a megadott cím rajta van az engedélyezett listán, hamarosan megérkezik a
                  postaládádba.
                </p>
                {IS_DEV && (
                  <div className="mt-3 rounded-md border border-[var(--app-divider)] bg-[var(--app-bg)] p-3 text-meta text-[var(--fg-2)]">
                    <p className="mb-2">
                      <strong className="text-[var(--ink-700)]">Dev környezet:</strong> az email a
                      lokális Mailhog-ba érkezik, nem a Gmail postafiókodba.
                    </p>
                    <a
                      href={MAILHOG_URL}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="inline-flex items-center gap-1 rounded-md border border-[var(--app-divider-2)] bg-[var(--n-0)] px-2.5 py-1 text-meta font-medium hover:bg-[var(--app-hover)]"
                    >
                      Mailhog megnyitása
                    </a>
                  </div>
                )}
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="email">Email-cím</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    required
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="te@morgens.hu"
                    autoFocus
                  />
                </div>
                {error && (
                  <p className="text-meta text-[var(--danger)]" role="alert">
                    {error}
                  </p>
                )}
                <Button
                  type="submit"
                  disabled={submitting || email.length === 0}
                  className="w-full"
                  size="lg"
                >
                  {submitting ? 'Küldés…' : 'Bejelentkezési link kérése'}
                </Button>
              </form>
            )}
          </div>

          <p className="mt-6 text-center text-meta text-[var(--fg-faint)]">
            Csak Morgens belsős e-mail címekkel.
          </p>
        </div>
      </div>
    </main>
  );
}

export const Route = createFileRoute('/login')({
  component: LoginPage,
});
